import { useMemo } from 'react';
import type { DiagnoseSub, Route, TabName } from '../router';
import { type SessionState, useSession } from './useSession';

/**
 * 이 계정이 볼 수 있는 것 (52회차).
 *
 * **판정은 관제 서버가 한다.** 여기는 화면이 서비스 선택기·메뉴를 그리는 편의일 뿐이다. 감춘 화면의
 * 주소를 직접 쳐도 서버가 403을 준다(`apps/server/src/auth/scope.ts`). 그래서 이 파일이 틀려도 남의
 * 서비스 자료가 새지는 않는다 — 없는 메뉴가 보이거나 403 문구가 뜰 뿐이다.
 *
 * 값은 로그인 응답과 `GET /api/auth/me`의 `admin`·`services`다. 로그인이 꺼진 서버와 그 칸을 안 보내는
 * 51회차 서버는 전체(관리자)로 읽는다 — 계약의 기본값과 같다.
 */

export interface Access {
  admin: boolean;
  /** 볼 수 있는 서비스. 관리자면 비어 있고 쓰지 않는다 */
  services: readonly string[];
}

export const FULL_ACCESS: Access = Object.freeze({ admin: true, services: Object.freeze([]) });

export function accessOf(state: SessionState): Access {
  if (state.status !== 'signedIn' || state.session === undefined) return FULL_ACCESS;
  if (state.session.admin !== false) return FULL_ACCESS;
  return { admin: false, services: [...(state.session.services ?? [])] };
}

export function useAccess(): Access {
  const session = useSession();
  return useMemo(() => accessOf(session), [session]);
}

/** 이 서비스를 볼 수 있나. 빈 이름(전체 보기)은 누구나 된다 — 서버가 허용 목록으로 좁힌다 */
export function canSeeService(access: Access, service: string): boolean {
  return access.admin || service === '' || access.services.includes(service);
}

/** 위 줄 메뉴. 관리 메뉴는 관리자에게만 */
export function visibleTabs(access: Access, tabs: readonly TabName[]): TabName[] {
  return access.admin ? [...tabs] : tabs.filter((tab) => tab !== 'admin');
}

/** 진단의 하위 탭. DB 감시는 관리자에게만 */
export function visibleDiagnoseSubs(access: Access, subs: readonly DiagnoseSub[]): DiagnoseSub[] {
  return access.admin ? [...subs] : subs.filter((sub) => sub !== 'db');
}

/** 이 화면을 그려도 되나. 관리 메뉴와 진단의 DB 감시는 관리자만이다 */
export function routeAllowed(access: Access, route: Route): boolean {
  if (access.admin) return true;
  if (route.name === 'admin') return false;
  return !(route.name === 'diagnose' && route.sub === 'db');
}

/** 서비스 선택기에 낼 이름. 일반 계정은 자료가 아직 없는 서비스도 허용 목록에 있으면 낸다 */
export function selectableServices(access: Access, known: readonly string[]): string[] {
  const names = access.admin
    ? [...known]
    : [...new Set([...access.services, ...known.filter((name) => access.services.includes(name))])];
  return names.sort((a, b) => a.localeCompare(b));
}

/**
 * 이 화면이 부를 조회에 실릴 서비스 (리뷰 R52-B-5·C-2). 전역 선택(`?service=`)은 부르는 쪽이 따로 보고,
 * 여기는 **주소 안에** 박힌 것만 꺼낸다 — 상세의 `svc=`, 요청 화면 조건의 `instanceService=`·`service=`.
 * 새 화면이 주소 조건으로 서비스를 받게 되면 여기에 더한다
 */
export function routeServices(route: Route): string[] {
  if (route.name === 'detail') return route.service === undefined ? [] : [route.service];
  if (route.name !== 'requests' || route.query === undefined) return [];
  const params = new URLSearchParams(route.query);
  return ['instanceService', 'service']
    .map((key) => params.get(key) ?? '')
    .filter((value) => value !== '');
}
